import { p as proxyCustomElement, H, c as createEvent, h, a as Host } from './index.js';
import { w as waitForAnimation } from './p-rUtJRH95.js';
import { c as createPopper, d as destroyPopper } from './p-D_OOeJgV.js';

const menuPaneCss = "@keyframes anim-open{0%{opacity:0}10%{opacity:0}to{opacity:1}}@keyframes anim-close{0%{opacity:1}90%{opacity:0}to{opacity:0}}:host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:none;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;position:absolute;z-index:var(--jw-z-index-menus)}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host([open]){animation:anim-open .25s ease forwards}:host([open][data-popper-placement^=top]) .content{animation:\"a\" .25s ease forwards}@keyframes a{0%{transform:translateY(16px)}to{transform:translateY(0)}}:host([open][data-popper-placement^=right]) .content{animation:\"b\" .25s ease forwards}@keyframes b{0%{transform:translateX(-16px)}to{transform:translateX(0)}}:host([open][data-popper-placement^=bottom]) .content{animation:\"c\" .25s ease forwards}@keyframes c{0%{transform:translateY(-16px)}to{transform:translateY(0)}}:host([open][data-popper-placement^=left]) .content{animation:\"d\" .25s ease forwards}@keyframes d{0%{transform:translateX(16px)}to{transform:translateX(0)}}:host(:not([open])){animation:anim-close .25s ease forwards}:host(:not([open])[data-popper-placement^=top]) .content{animation:\"e\" .25s ease forwards}@keyframes e{0%{transform:translateY(0)}to{transform:translateY(16px)}}:host(:not([open])[data-popper-placement^=right]) .content{animation:\"f\" .25s ease forwards}@keyframes f{0%{transform:translateX(0)}to{transform:translateX(-16px)}}:host(:not([open])[data-popper-placement^=bottom]) .content{animation:\"g\" .25s ease forwards}@keyframes g{0%{transform:translateY(0)}to{transform:translateY(-16px)}}:host(:not([open])[data-popper-placement^=left]) .content{animation:\"h\" .25s ease forwards}@keyframes h{0%{transform:translateX(0)}to{transform:translateX(16px)}}:host([data-popper-placement^=left]),:host([data-popper-placement^=right]){flex-direction:row}:host([data-popper-placement^=bottom]),:host([data-popper-placement^=top]){flex-direction:column}:host([data-popper-placement$=start]) .content{align-self:flex-start}:host([data-popper-placement$=stretch]) .content{align-self:stretch}:host([data-popper-placement$=end]) .content{align-self:flex-end}:host([stretch=fixed][data-popper-placement^=left]) .content,:host([stretch=fixed][data-popper-placement^=right]) .content{overflow-y:scroll}:host([stretch=fixed][data-popper-placement^=bottom]) .content,:host([stretch=fixed][data-popper-placement^=top]) .content{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.content{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;background-color:var(--jw-theme-surface-elevated-default-bg-color);border:1px solid var(--jw-theme-surface-border-border-color);border-radius:.25rem;box-shadow:var(--jw-theme-effects-shadow-shadow-lg-box-shadow);box-sizing:border-box;cursor:default!important;display:flex;flex:1;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-style:normal;font-weight:400;height:100%;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;margin:0;min-height:32px;min-width:32px;padding:0;text-transform:none;width:100%}.content *,.content :after,.content :before{box-sizing:inherit}.content img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}::slotted(wui-check-list-input),::slotted(wui-list){padding:.5rem;width:auto}";

const WuiMenuPane = /*@__PURE__*/ proxyCustomElement(class WuiMenuPane extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.clickOutside = createEvent(this, "clickOutside", 3);
        this.openChanged = createEvent(this, "openChanged", 3);
        /**
         * If opened
         */
        this.open = false;
        /**
         * Where to place relative to its parent
         */
        this.placement = 'auto';
        /**
         * If should stretch to parent width/height
         *
         * 'off': Disable stretching
         * 'fit': Take parent width/height
         * 'min': Take at least parent width/height
         * 'max': Take at most parent width/height
         * 'fixed': Grows up to a fixed width/height. Useful for menus and dropdowns.
         */
        this.stretch = 'min';
        this.handleClickOutside = (e) => {
            const composedPath = e.composedPath();
            const target = composedPath[0];
            if (!target) {
                return;
            }
            if (composedPath.includes(this.elm) || this.elm.contains(target)) {
                return;
            }
            this.clickOutside.emit(e);
        };
    }
    watchOpen(newValue) {
        if (newValue) {
            this.show();
        }
        else {
            this.hide();
        }
    }
    _listenKeyDown(e) {
        this.handleKeyDown(e);
    }
    /**
     * Forward a keydown KeyboardEvent to be handled by this component.
     *
     * Handles Escape to close menu.
     * Handles arrow up/down and Enter to open menu.
     *
     * @param e KeyboardEvent (should be keydown)
     * @returns Returns true when the event was handled
     */
    async handleKeyDown(e) {
        // ⚠️ When successfully handling a keyboard event, use `preventDefault` to stop it from doing anything else
        // Close menu
        if (this.open && e.key === 'Escape') {
            this.hide();
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
        // Open menu
        const openKeys = ['ArrowDown', 'ArrowUp', 'Enter'];
        if (!this.open && openKeys.includes(e.key)) {
            this.show();
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
        return false;
    }
    show() {
        const p = waitForAnimation(this.elm, 'animation').then(() => {
            document.addEventListener('click', this.handleClickOutside);
        });
        // reset display to make sure el.offsetParent is correct because
        // popper needs it calculate position
        this.elm.style.display = 'none';
        this.popperInstance = createPopper(this.elm, {
            placement: this.placement,
            stretch: this.stretch,
            offset: 4,
        });
        this.elm.style.display = 'flex';
        this.open = true;
        this.openChanged.emit(true);
        return p;
    }
    hide() {
        document.removeEventListener('click', this.handleClickOutside);
        const p = waitForAnimation(this.elm, 'animation').then(() => {
            if (!this.open) {
                this.elm.style.display = 'none';
                this.popperInstance = destroyPopper(this.popperInstance);
            }
        });
        this.open = false;
        this.openChanged.emit(false);
        return p;
    }
    async disconnectedCallback() {
        if (this.open) {
            await this.hide();
        }
    }
    componentDidLoad() {
        if (this.open) {
            this.show();
        }
    }
    render() {
        return (h(Host, { key: 'ee60376aabc78ac9b93922fa52979075f049e9ce', role: "menu" }, h("div", { key: 'a0f0f81e59ea18d3eeb951f476da85f0b83fb0c4', class: "content" }, h("slot", { key: '462806f26b14e2e5239c1d71e33c612099c36ba2' }))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "open": ["watchOpen"]
    }; }
    static get style() { return menuPaneCss; }
}, [257, "wui-menu-pane", {
        "open": [1540],
        "placement": [1],
        "stretch": [513],
        "handleKeyDown": [64]
    }, [[0, "keydown", "_listenKeyDown"]], {
        "open": ["watchOpen"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-menu-pane"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiMenuPane);
            }
            break;
    } });
}
defineCustomElement();

export { WuiMenuPane as W, defineCustomElement as d };
